import { lazy, Suspense } from 'react';
import { PermissionGuard } from '@emfood/shared';
import { Outlet, createRoute, SearchSchemaInput } from '@tanstack/react-router';

import { PERMISSIONS } from 'src/api/roles-config';
import DashboardLayout from 'src/layouts/dashboard';

import AppAuthGuard from 'src/components/auth/app-auth-guard';
import { SplashScreen, LoadingScreen } from 'src/components/loading-screen';

import { ProductsListSearchParamsSchema } from 'src/sections/product/product-list/search-params';

import { rootRoute } from './root';

const EditProductDraft = lazy(() => import('src/sections/product/edit-draft/view'));
const ProductsList = lazy(() => import('src/sections/product/product-list/view'));
const EditProduct = lazy(() => import('src/sections/product/edit-product/view'));
const ProductDetails = lazy(() => import('src/sections/product/product-details/view'));

const productsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'products',
  component: () => (
    <AppAuthGuard splashScreen={<SplashScreen />}>
      <PermissionGuard requiredPermissions={[PERMISSIONS.PRODUCTS_READ]}>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </PermissionGuard>
    </AppAuthGuard>
  ),
});

const productsIndexRoute = createRoute({
  getParentRoute: () => productsRoute,
  path: '/',
  component: ProductsList,
  validateSearch: (search: unknown & SearchSchemaInput) =>
    ProductsListSearchParamsSchema.validateSync(search),
});

const productDraftRoute = createRoute({
  getParentRoute: () => productsRoute,
  path: 'draft/$id',
  component: () => (
    <PermissionGuard requiredPermissions={[PERMISSIONS.PRODUCTS_WRITE]}>
      <EditProductDraft />
    </PermissionGuard>
  ),
});

const productEditRoute = createRoute({
  getParentRoute: () => productsRoute,
  path: '$id/edit',
  component: () => (
    <PermissionGuard requiredPermissions={[PERMISSIONS.PRODUCTS_WRITE]}>
      <EditProduct />
    </PermissionGuard>
  ),
});

const productDetailsRoute = createRoute({
  getParentRoute: () => productsRoute,
  path: '$id',
  component: ProductDetails,
});

export const productsRouteTree = productsRoute.addChildren([
  productsIndexRoute,
  productDraftRoute,
  productEditRoute,
  productDetailsRoute,
]);
